import { Injectable } from '@angular/core';
import { Subject, Observer, Observable } from 'rxjs';
import {FactSessionId, ProjectId} from "../../types/ids";
import {AuthService} from "../auth.service";
import {WebmateOptions} from "../../../webmate-options";


/** Normal, real WebSocket Service */
@Injectable({
  providedIn: 'root'
})
export class WebSocketService{

    constructor(private authService: AuthService, private webmateOptions: WebmateOptions){
    }

    public connectToEventsForProjects(projectIds: ProjectId[]): Subject<MessageEvent> {
        return this._connect(this.authService.addAuthAsQueryParameter(this.webmateOptions.websocketUri + "/projects/events?projects=" + projectIds.join(",")));
    }

    public connectToFactSessionFacts(factSessionId: FactSessionId): Subject<MessageEvent> {
        return this._connect(this.authService.makeAuthorizedWebSocketUrl(`/fact/factsessions/${factSessionId}/facts`));
    }

    /** Connects to the given target, creating a 'PublishSubject' (called only 'Subject in JS). A Subject contains an Observable allowing to receive data from
     * the Socket and an Observer that can be used to push data into the WebSocket. *NOTE* that is Subject is not cached in the WebSocket-Service, each call to
     * this function will open a new Connection! Furthermore, *NOTE* that the Subject may start to send Events immediately and does not repeat previous events
     * for a new Subscriber...
     *
     * Ping/Pong messages: This Service will send recurring "WMWebSocketPing" messages to the backend service, which in turn will
     * answer with a "WMWebSocketPong" message. This message is exposed via the normal Observable methods and has to be handled by all event subscribers.
     * */
    private _connect(target: string): Subject<MessageEvent> {
        let socket = new WebSocket(target);

        let lastMsg = Date.now();
        const timeBetweenPings = 5000;
        /* Setup recurring ping messages */
        function _ping(): void {
            if (socket.readyState === WebSocket.OPEN && Date.now() - lastMsg >= timeBetweenPings) {
                socket.send("WMWebSocketPing");
            }
        }
        let pingInterval = setInterval(_ping, timeBetweenPings);

        let observer = {
            next: (data: Object) => { //send data to the WebSocket
                if (socket.readyState === WebSocket.OPEN) {
                    socket.send(JSON.stringify(data));
                } else console.error("Cannot send via non-ready WebSocket, state is '" + socket.readyState);
            },
            error: (error: any) => {
                console.warn("Error for WebSocket '" + target + "' with error:", error, "This is ignored.");
            },
            complete: () => {
                socket.close(1000, "Javascript closed Connection");
            }
        };
        let observable = Observable.create(
            (observerOfObservable: Observer<MessageEvent>) => {
                socket.onerror = function(error: any){  //note: we never inform the observable of Errors, WebSocket-Errors are encapsulated in this Service!
                    console.warn("WebSocket connection to '" + target + "' had an error:", error);
                    socket.close(1000, "Closed due to Error"); //okay, simulate normal close...
                    clearInterval(pingInterval);
                };
                socket.onclose = function(){
                    observerOfObservable.complete(); //we do not reconnect (here), inform anyone using this thing of the Termination by closing the Observable
                    observer.complete(); //logs this and attempts closes the socket from our side too (which is kind of redundant in this case)
                    clearInterval(pingInterval);
                };

                /** normal case, basically pipe all messages through to the Observable */
                socket.onmessage = function(msg){
                    lastMsg = Date.now();
                    observerOfObservable.next.bind(observerOfObservable)(msg);
                };

                /** Close the Socket if the Subscriber unsubscribes from the Observable (and thus transitively from
                 * the Socket)! In Short: If no one listens anymore, we close the connection to the BackEnd... */
                return socket.close.bind(socket);
            }
        );
        return Subject.create(observer, observable);
    }
}
